import React from 'react';
import { format, isToday, type Locale } from 'date-fns';
import { styled } from 'styled-components';
import { useTranslation } from 'react-i18next';
import colors from '../../../styles/colors';
import { isTimeSlotSameDayAsSelectedSlot } from '../../../helpers/dateHelper';
import { type Slot } from '../../../services/appointment/model';
import { getLocale } from '../../../helpers/localeHelper';
import { preventEnterSubmit } from '../../../helpers/formHelper';
import { mediaQueries } from '../../../styles/mediaQueries';

interface DayComponentProps {
  date: string;
  slot: Slot | undefined;
  onSelectDate: (date: string) => void;
  selected: boolean;
  showReplacementServiceSlots: boolean;
  replacementServiceSlot?: Slot | undefined;
  loading?: boolean;
}

interface WeekComponentProps {
  days: {
    date: string;
    slot: Slot | undefined;
    replacementServiceSlot?: Slot | undefined;
  }[];
  onSelectSlot: (date: string) => void;
  selectedDate: string;
  showReplacementServiceSlots: boolean;
  loading?: boolean;
}

const StyledContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  place-items: center;
  gap: 4px;
  padding: 6px 0;

  ${mediaQueries.landscapePhoneUp} {
    padding: 8px 0;
  }
`;

const VisuallyHiddenRadio = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;

  &:focus {
    outline: none;
  }
`;

const StyledDateLabel = styled.label<{
  $selected: boolean;
  $isCurrent: boolean;
  $disabled: boolean;
}>`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  font-size: clamp(0.75rem, 2.5vw, 0.875rem);
  font-weight: 400;
  min-height: 2rem;
  min-width: 2rem;
  width: clamp(2rem, 100%, 4rem);
  aspect-ratio: 1;
  border-radius: 4rem;
  border: 2px solid
    ${({ $disabled, $selected, $isCurrent }) =>
      !$disabled && ($selected || $isCurrent)
        ? colors.interactive.borderEmphasised
        : colors.interactive.default};
  box-sizing: border-box;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  background: ${({ $selected, $disabled }) =>
    $disabled
      ? colors.background.default
      : $selected
        ? colors.interactive.emphasised
        : colors.interactive.default};
  color: ${({ $selected, $disabled }) =>
    $disabled
      ? colors.text.disabled
      : $selected
        ? colors.static.white
        : colors.text.black};

  &:hover {
    ${({ $disabled, $selected, $isCurrent }) =>
      !$disabled &&
      `
      background: ${$selected ? colors.interactive.emphasisedHover : colors.interactive.defaultHover};
      border: 2px solid ${
        $selected || $isCurrent
          ? colors.interactive.borderEmphasised
          : colors.interactive.defaultHover
      };
    `}
  }

  &:focus-within {
    outline: 3px solid ${colors.interactive.borderEmphasised};
    outline-offset: 2px;
  }
`;

const StyledDot = styled.span`
  position: relative;
  width: 8px;
  height: 8px;
  top: 4px;
  border-radius: 100%;
`;

const StyledDotsRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 2px;
`;

const GreenDot = styled(StyledDot)<{ $selected?: boolean }>`
  background: ${({ $selected }) =>
    $selected ? colors.background.default : colors.static.green};
`;

const getAriaLabel = (
  date: string,
  ariaTodayWord: string,
  locale: Locale,
): string =>
  isToday(date)
    ? `${ariaTodayWord} ${format(date, 'EEEE dd MMMM yyyy', { locale })}`
    : format(date, 'EEEE dd MMMM yyyy', { locale });

const DayComponent: React.FC<DayComponentProps> = ({
  date,
  slot,
  onSelectDate,
  selected,
  replacementServiceSlot,
  showReplacementServiceSlots,
  loading,
}) => {
  const { t } = useTranslation();
  const locale = getLocale();
  const originalServiceDate = slot ? slot.date : '';
  const replacementServiceDate = replacementServiceSlot
    ? replacementServiceSlot?.date
    : '';
  const selectableDate = originalServiceDate || replacementServiceDate;
  const isDisabled =
    (!slot && !showReplacementServiceSlots) ||
    (!slot && !replacementServiceSlot) ||
    !!loading;
  const hasAvailableSlot =
    slot || (replacementServiceSlot && showReplacementServiceSlots);
  const ariaTodayWord = t('pages.timeslots.ariaTodayWord');
  const timeSlotsCount = showReplacementServiceSlots
    ? replacementServiceSlot?.timeSlots?.length || slot?.timeSlots?.length || 0
    : slot?.timeSlots?.length || 0;

  const handleChange = React.useCallback(() => {
    onSelectDate(selectableDate);
  }, [onSelectDate, selectableDate]);

  const ariaLabel = timeSlotsCount
    ? `${getAriaLabel(date, ariaTodayWord, locale)}, ${t('pages.timeslots.ariaAvailableTimesCount', { count: timeSlotsCount })}`
    : getAriaLabel(date, ariaTodayWord, locale);

  return (
    <StyledDateLabel
      $selected={selected}
      $isCurrent={isToday(date)}
      $disabled={isDisabled}
      data-cy={`slot-${format(date, 'yyyy-MM-dd', { locale })}`}
    >
      {!isDisabled && (
        <VisuallyHiddenRadio
          type="radio"
          name="date"
          value={date}
          checked={selected}
          onChange={handleChange}
          onKeyDown={preventEnterSubmit}
          aria-label={ariaLabel}
        />
      )}
      {format(date, 'dd', { locale })}
      <StyledDotsRow>
        {hasAvailableSlot && (
          <GreenDot $selected={selected} aria-hidden="true" />
        )}
      </StyledDotsRow>
    </StyledDateLabel>
  );
};

const WeekComponent: React.FC<WeekComponentProps> = ({
  days,
  onSelectSlot,
  selectedDate,
  showReplacementServiceSlots,
  loading,
}) => (
  <StyledContainer>
    {days.map((day) => (
      <DayComponent
        key={day.date}
        {...day}
        selected={isTimeSlotSameDayAsSelectedSlot(day.date, selectedDate)}
        onSelectDate={onSelectSlot}
        showReplacementServiceSlots={showReplacementServiceSlots}
        loading={loading}
      />
    ))}
  </StyledContainer>
);
export default WeekComponent;
